import {
  ControlBarWrapper,
  CustomControlWrapper,
  AbsoluteCenter,
  CustomControlButtonList,
  ScreenPlayContainer,
  GradientX,
} from '../../Player/styles';
import ControlBarFullScreen from './ControlBarFullScreen';
import ControlBarProgress from './ControlBarProgress';
import { useEffect, useRef, useState } from 'react';
import PlayControls from './PlayControls';
import RemindDuration from './RemindDuration';
import TitleBar from './TitleBar';
import ActionsBar from './ActionsBar';
import ControlBarNextPrevEpisode from './ControlBarNextPrevEpisode';
import ControlBarInfo from '../../Player/components/ControlBarInfo';
import Watermark from './Watermark';
import SeasonsAndEpisodes from './Episodes';
import Setting from './Setting/Setting';
import { Flex, Spinner } from '../../../styles';
import UsePlayerProps from '../../../Store/usePlayerProps';
import ControlBarVolumeController from './ControlBarVolumeController';
import ControlBarCaptionSwitch from './ControlBarCaptionSwitch';
import theme from '../../../theme';

export default function PlayerCustomComponents({ player, isShort, onShowInfo }:
  { player: HTMLVideoElement; isShort: boolean; onShowInfo?: () => void }) {
  const playerGlobalState = UsePlayerProps((state) => state.playerState);
  const { contentData, contentConfigs, sources } = playerGlobalState;
  const [showControlBar, setShowControlBar] = useState<boolean>(false);
  const [activePanel, setActivePanel] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const timer = useRef<any>();
  const [FullScreen, setFullscreen] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const IS_IOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  useEffect(() => {
    handleMobileBreakpoint();
    player.addEventListener('waiting', handleLoading);
    player.addEventListener('playing', handlePlaying);
    window.addEventListener('resize', handleMobileBreakpoint);
    document.addEventListener('touchmove', handleMouseMove);
    if (IS_IOS) {
      document.addEventListener('click', handleMouseMove);
    } else {
      document.addEventListener('keydown', handleMouseMove);
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mousedown', handleMouseMove);
    }
    return () => {
      clearTimeout(timer.current);
      timer.current = null;
      player.removeEventListener('waiting', handleLoading);
      player.removeEventListener('playing', handlePlaying);
      window.removeEventListener('resize', handleMobileBreakpoint);
      document.removeEventListener('touchmove', handleMouseMove);
      if (IS_IOS) {
        document.removeEventListener('click', handleMouseMove);
      } else {
        document.removeEventListener('keydown', handleMouseMove);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mousedown', handleMouseMove);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMobileBreakpoint = () => {
    setIsMobile(Number(theme.breakpoints.md) > Number(window.innerWidth));
    setIsTablet(Number(theme.breakpoints.lg) > Number(window.innerWidth));
  };

  const handleLoading = () => {
    setLoading(true);
  };

  const handlePlaying = () => {
    setLoading(false);
  };

  const handleMouseMove = () => {
    const playerWrapper = document.getElementById('custom-player-wrapper');
    if (!playerWrapper) return;
    setShowControlBar(true);
    playerWrapper.style.cursor = 'default';
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setShowControlBar(false);
      playerWrapper.style.cursor = 'none';
    }, 5000);
  };

  const handleScreenClick = () => {
    // Shorts toggle on tap at every size; the full-page player keeps controls-only on touch.
    if (activePanel || (isMobile && !isShort)) return;
    if (player.paused) {
      player.play();
    } else {
      player.pause();
    }
  };

  const handleFullScreen = () => {
    setFullscreen(!FullScreen);
    if (player.paused) {
      player.play();
    }
  };

  return (
    <>
      <ScreenPlayContainer onClick={handleScreenClick} onDoubleClick={isShort ? undefined : handleFullScreen}></ScreenPlayContainer>
      {!showControlBar && !activePanel && <Watermark />}
      {loading && (
        <AbsoluteCenter>
          <Spinner block size={isMobile ? isTablet ? 'medium' : 'small' : 'big'} color="primary" />
        </AbsoluteCenter>
      )}
      {!isShort && <ActionsBar player={player} />}
      <CustomControlWrapper show={showControlBar || activePanel}>
        <RemindDuration player={player} />
        <PlayControls player={player} isShort={isShort} />
        <TitleBar isShort={isShort} />
        <GradientX />
        <ControlBarWrapper>
          <ControlBarProgress player={player} playingAd={false} />
          <CustomControlButtonList>
            <Flex>
              {contentConfigs && contentData?.content?.type === 'episode' &&
                <ControlBarNextPrevEpisode />}
              {!!contentData?.seasons?.length &&
                <SeasonsAndEpisodes emitActivePanel={(status) => setActivePanel(status)} />}
              <ControlBarVolumeController player={player} />
              {onShowInfo && <ControlBarInfo onClick={onShowInfo} />}
            </Flex>
            <Flex>
              {/* Shorts have no playback settings — see the note in the video.js player. */}
              {!isShort && (IS_IOS
                ? !!(sources?.captions?.length) && <ControlBarCaptionSwitch player={player} />
                : !!(sources?.captionFiles?.length) && sources?.captionFiles?.length > 1 && <ControlBarCaptionSwitch player={player} />)}
              {!isShort && (
                <Setting
                  player={player}
                  changeCaptionPosition={showControlBar}
                  emitActivePanel={(status) => setActivePanel(status)}
                />
              )}
              {!isShort &&
                <ControlBarFullScreen fullScreenState={FullScreen} hideControls={() => {
                  setShowControlBar(false);
                  clearTimeout(timer.current);
                }} />}
            </Flex>
          </CustomControlButtonList>
        </ControlBarWrapper>
      </CustomControlWrapper>
    </>
  );
}
